// ScopePicker — the searchable single-select behind the Event / Market /
// User filters shared by every Activity page.
//
// A plain <Select> can't do this job: events and markets number in the
// hundreds and users in the thousands, so the option list has to come
// from a server-side search rather than being enumerated up front. It is
// the same interaction UserPicker uses on the transfer page, compacted
// into something that sits in a filter row: once a value is picked the
// input collapses to a chip with a clear button, so the bar doesn't grow
// a card per filter.
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui/Input";

export interface ScopeOption {
  id: string;
  label: string;
  /** Second line — an email, a ULID, whatever disambiguates duplicates. */
  sublabel?: string;
}

export interface ScopePickerProps {
  label: string;
  placeholder: string;
  /** The current selection, or null. Owned by the caller. */
  selected: ScopeOption | null;
  onSelect: (option: ScopeOption | null) => void;
  /** Search text the caller feeds to its own query. */
  query: string;
  onQueryChange: (q: string) => void;
  options: ScopeOption[];
  loading?: boolean;
  disabled?: boolean;
  /** Shown instead of the result list when the picker is inert. */
  disabledHint?: string;
  className?: string;
}

export function ScopePicker({
  label,
  placeholder,
  selected,
  onSelect,
  query,
  onQueryChange,
  options,
  loading = false,
  disabled = false,
  disabledHint,
  className,
}: ScopePickerProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (selected) {
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <span className="text-2xs font-semibold uppercase tracking-wide text-t-3">
          {label}
        </span>
        <div className="flex h-10 items-center gap-2 rounded-md border border-line-1 bg-bg-2 px-3">
          <span className="min-w-0 flex-1 truncate text-sm text-t-1" title={selected.sublabel || selected.label}>
            {selected.label}
          </span>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Clear ${label} filter`}
            onClick={() => {
              onSelect(null);
              onQueryChange("");
              // Refocus so "change the filter" flows straight into a new
              // search instead of costing a second click.
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className="shrink-0 text-xs text-t-3 transition-colors hover:text-t-1 disabled:opacity-50"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // Only show the dropdown while the field has focus — otherwise every
  // picker on the bar would keep a list open at once.
  const showList = focused && !disabled && query.trim().length >= 2;

  return (
    <div className={cn("relative flex flex-col gap-1.5", className)}>
      <Input
        ref={inputRef}
        label={label}
        value={query}
        disabled={disabled}
        placeholder={disabled && disabledHint ? disabledHint : placeholder}
        onChange={(e) => onQueryChange(e.target.value)}
        onFocus={() => setFocused(true)}
        // Blur fires before the option's click handler, so defer the
        // close past the click or every selection would be swallowed.
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {showList && (
        <div className="absolute top-full z-20 mt-1 w-full rounded-md border border-line-1 bg-bg-1 shadow-lg">
          {loading ? (
            <p className="px-3 py-2 text-xs text-t-3">Searching…</p>
          ) : options.length > 0 ? (
            <ul className="max-h-64 overflow-y-auto py-1">
              {options.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(o);
                      onQueryChange("");
                    }}
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-bg-2"
                  >
                    <span className="block w-full truncate text-sm text-t-1">{o.label}</span>
                    {o.sublabel && (
                      <span className="block w-full truncate font-mono text-[10px] text-t-3">
                        {o.sublabel}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-2 text-xs text-t-3">No matches for “{query.trim()}”.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Debounce a search box so a filter doesn't fire a request per keystroke. */
export function useDebouncedQuery(value: string, delay = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
